import { css } from '@emotion/react';
import { Link as RouterLink } from 'react-router-dom';
import MuiLink from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

import RunStateLabel from '../WorkflowRunsPage/Runs/RunStateLabel';
import TaskStateBar from 'components/TaskStateBar/TaskStateBar';
import WorkflowUserName from 'components/WorkflowUserName/WorkflowUserName';

import useAutofetchedAccountsAsync from 'store/useAutofetchedAccountsAsync';
import useDashboardUsersAsync from 'store/useDashboardUsersAsync';
import useProjectsAsync from 'store/useProjectsAsync';

import formatWorkflowUser from 'utils/formatWorkflowUser';

import IRunEntry from 'types/IRunEntry';
import IColumnDefinition from '../WorkflowRunsPage/Table/IColumnDefinition';

import { format } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';

function formatDate(date: string) {
  return format(new Date(date), 'y MMM d, HH:mm', { locale: enUS });
}

export function useDashboardColumns(onShowErrors: (run: IRunEntry) => void): IColumnDefinition<IRunEntry>[] {
  const theme = useTheme();

  const projectsAsync = useProjectsAsync();
  const projects = projectsAsync.status === 'SUCCESS' || projectsAsync.status === 'REFRESH' ? projectsAsync.data : [];
  const projectNamesById = new Map(projects.map((project) => [project.id, project.name]));

  const usersAsync = useDashboardUsersAsync();
  const users = usersAsync.status === 'SUCCESS' || usersAsync.status === 'REFRESH' ? usersAsync.data : [];
  const usersById = new Map(users.map((user) => [user.id, user]));

  const accountsAsync = useAutofetchedAccountsAsync();
  const accounts = accountsAsync.status === 'SUCCESS' || accountsAsync.status === 'REFRESH' ? accountsAsync.data : [];
  const accountsById = new Map(accounts.map((account) => [account.id, account]));

  return [
    {
      header: 'Name',
      key: 'name',
      render: (run) => (
        <MuiLink component={RouterLink} to={`/projects/${run.project_id}/workflows/${run.id}/details`}>
          {run.name}
        </MuiLink>
      ),
    },
    {
      header: 'Project',
      key: 'project_id',
      render: (run) => (
        <MuiLink component={RouterLink} to={`/projects/${run.project_id}/workflows`}>
          {projectNamesById.get(run.project_id) ?? run.project_id}
        </MuiLink>
      ),
    },
    {
      header: 'Date',
      key: 'updated_utc',
      render: (run) => formatDate(run.updated_utc),
    },
    {
      header: 'User',
      key: 'user_id',
      render: (run) => {
        const { name, isAccountWorker } = formatWorkflowUser(
          run.user_id,
          usersById.get(run.user_id),
          accountsById.get(run.user_id),
        );
        return <WorkflowUserName name={name} isAccountWorker={isAccountWorker} />;
      },
    },
    {
      header: 'State',
      key: 'state',
      render: (run) => (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
          `}
        >
          <RunStateLabel state={run.state} />
          {run.has_worker_lost_tasks && (
            <Tooltip title="This workflow has tasks whose worker was lost">
              <span
                css={css`
                  color: ${theme.palette.warning.main};
                  font-size: 0.75rem;
                  font-weight: 600;
                `}
              >
                WORKER LOST
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      header: 'Tasks',
      key: 'task_state_count',
      render: (run) => <TaskStateBar counts={run.task_state_count ?? {}} />,
    },
    {
      header: '',
      // @ts-expect-error – Never mind that there is no "errors" property on IRunEntry
      key: 'errors',
      render: (run) =>
        (run.task_state_count?.FAILED ?? 0) > 0 && (
          <MuiLink component="button" onClick={() => onShowErrors(run)}>
            Errors
          </MuiLink>
        ),
    },
  ];
}
